import { useEffect, useMemo, useState } from 'react';
import { apiClient } from '@/lib/apiClient';

/**
 * Shared-хук: батч-запрос короткоживущих signed-URL для ключей фото медиа
 * (`primaryPhotoKey` декоров, `imageFilename` фурнитуры) → map `key → url` для
 * рендера `<img>`. Единая реализация для Блока 2 (декоры) и Блока 5 (фурнитура)
 * после дедупа B-222 (раньше — два байт-идентичных хука `useDecorImageUrls` и
 * `useFurnitureImageUrls`). Декоровый хук остаётся тонким ре-экспорт-делегатом
 * (R1: 5 call-site'ов Блока 2 не трогаются), фурнитура импортирует напрямую.
 *
 * - Принимает список ключей видимых карточек (`string | null | undefined`).
 *   `null`/`undefined` (нет фото) — отбрасываются; дубликаты схлопываются.
 * - Батчинг: контракт `POST /api/media/signed-url` принимает 1..50 ключей за
 *   запрос (P-011). Список (каталог накапливает страницы) бьётся на чанки ≤50 и
 *   запрашивается параллельно, результаты мёржатся.
 * - Состояния: `loading` (идёт запрос), `error` (любой чанк упал). Частичный
 *   успех сохраняется: `urls` содержит то, что удалось подписать.
 * - MVP: fetch на изменение набора ключей; refetch на истечение TTL (3600с) —
 *   ВНЕ объёма (битый `<img>` → onError → плейсхолдер в карточке).
 *
 * Security: signed-URL держатся только в state (память сессии), не логируются и
 * не персистятся (короткоживущие токены). P-009 — фото пользователя тут не при чём.
 */

const MAX_BATCH = 50;

export interface UseMediaImageUrlsResult {
  /** key → signed url. Отсутствие ключа = нет подписи (ещё грузится / ошибка / нет фото). */
  urls: Record<string, string>;
  loading: boolean;
  error: boolean;
}

function chunk<T>(arr: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

export function useMediaImageUrls(
  keys: readonly (string | null | undefined)[],
): UseMediaImageUrlsResult {
  // Уникальные не-null ключи, стабильно отсортированы → стабильный dep.
  const uniqueKeys = useMemo(() => {
    const set = new Set<string>();
    for (const k of keys) if (k) set.add(k);
    return [...set].sort();
  }, [keys]);
  // JSON.stringify — стабильный dep без коллизий (ключи содержат пробелы/спецсимволы).
  const keysKey = JSON.stringify(uniqueKeys);

  const [urls, setUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (uniqueKeys.length === 0) {
      setUrls({});
      setLoading(false);
      setError(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);

    const batches = chunk(uniqueKeys, MAX_BATCH);
    Promise.allSettled(batches.map((batch) => apiClient.media.signedUrls({ keys: batch }))).then(
      (results) => {
        if (cancelled) return;
        const merged: Record<string, string> = {};
        let anyError = false;
        for (const r of results) {
          if (r.status === 'fulfilled') {
            for (const [key, entry] of Object.entries(r.value.urls)) merged[key] = entry.url;
          } else {
            anyError = true;
          }
        }
        setUrls(merged);
        setError(anyError);
        setLoading(false);
      },
    );

    return () => {
      cancelled = true;
    };
    // keysKey — стабильная сериализация uniqueKeys (массив меняет identity каждый рендер).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keysKey]);

  return { urls, loading, error };
}
